import { ZRoomColoringId } from "@multiconsult-group/multiplacer-core";
import { createSelector } from "@reduxjs/toolkit";
import {
  ERoomColoringCategoryId,
  IRoomColoring,
  VALIDATION_DECISION_COLOR_SPECS,
  makeValidationDecisionColorFn,
  DROFUS_OCCURRENCE_VALIDATION_COLOR_SPECS,
  makeStandardRoomValidationColorFn,
  makeSpecifiedArticlesValidationColorFn,
} from "../RoomColoring";
import { ERoomColoringSubcategoryId } from "../RoomColoring/roomColoringConstants";
import {
  selectHttpGetRoomsCurrentFloorResult,
  selectHttpRoomsBasedOnActiveProjectTab,
} from "./httpRoomSelectors";
import {
  DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID,
  makeDrofusOccurrenceValidationListTypeColoringId,
  makeDrofusOccurrenceValidationResponsibilityColoringId,
  parseDrofusOccurrenceValidationColoringId,
  titleForDrofusOccurrenceValidationColoringId,
} from "../DrofusOccurrenceValidation/drofusOccurrenceValidationIds";

/* -------------------------------------------------------------------------- */
/*  Specified Articles → dynamiske fargefilter                                 */
/* -------------------------------------------------------------------------- */

export const selectUniqueSpecifiedArticlesKeys = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsHashmap): string[] => {
    const unique = new Set<string>();

    // Always include overall
    unique.add(DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID);

    Object.values(roomsHashmap).forEach((room) => {
      const perListType = room?.validation?.specifiedArticles?.perListType;
      const perResp = room?.validation?.specifiedArticles?.perResponsibility;

      if (perListType && typeof perListType === "object") {
        Object.keys(perListType).forEach((listType) => {
          unique.add(makeDrofusOccurrenceValidationListTypeColoringId(listType));
        });
      }

      if (perResp && typeof perResp === "object") {
        Object.keys(perResp).forEach((respKey) => {
          unique.add(makeDrofusOccurrenceValidationResponsibilityColoringId(respKey));
        });
      }
    });

    return Array.from(unique).sort((a, b) => {
      // Put overall first
      if (a === DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID) return -1;
      if (b === DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID) return 1;

      const aScope = parseDrofusOccurrenceValidationColoringId(a);
      const bScope = parseDrofusOccurrenceValidationColoringId(b);
      if (aScope?.type !== bScope?.type) {
        if (aScope?.type === "listType") return -1;
        if (bScope?.type === "listType") return 1;
      }
      return a.localeCompare(b);
    });
  }
);

export const selectSpecifiedArticlesValidationColorItems = createSelector(
  [selectUniqueSpecifiedArticlesKeys],
  (keys): IRoomColoring[] => {
    return keys.map((key) => {
      const scope = parseDrofusOccurrenceValidationColoringId(key);
      const isOverall = !scope || scope.type === "overall";
      const categoryId =
        scope?.type === "responsibility"
          ? ERoomColoringCategoryId.DrofusForekomstValideringAnsvar
          : ERoomColoringCategoryId.DrofusForekomstValideringListe;
      const trendKeyPath = isOverall
        ? `validation.specifiedArticles.summary.matchRatePct`
        : undefined;

      return {
        id: ZRoomColoringId.parse(key),
        title: titleForDrofusOccurrenceValidationColoringId(key),

        categoryId,
        subcategoryId: ERoomColoringSubcategoryId.ValideringStatus,
        order: isOverall ? 0 : scope?.type === "listType" ? 10 : 20,
        tags: ["drofus", "forekomst", "artikkel", "validering"],

        explanation: {
          shortNo: "Fargelegger rom etter treffprosent for forekomstvalidering (intervaller).",
          longNo:
            "Dette viser hvor stor andel av programmert artikkelgrunnlag som matcher forekomstene i rommet. Filter kan avgrenses på dRofus-liste eller ansvar.",
          docKey: "colorfilters/specified-articles",
        },

        colorSpecs: DROFUS_OCCURRENCE_VALIDATION_COLOR_SPECS,
        trendKeyPath,
        getPolygonColors: makeSpecifiedArticlesValidationColorFn(
          key as `specifiedArticles.${string}`
        ),
      };
    });
  }
);

/* -------------------------------------------------------------------------- */
/*  Standardrom-validering → dynamiske fargefilter                             */
/* -------------------------------------------------------------------------- */

export const selectUniqueStandardRoomValidationsKeys = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab],
  (roomsHashmap): string[] => {
    const unique = new Set<string>();

    Object.values(roomsHashmap).forEach((room) => {
      const standardRoom = room?.validation?.standardRoom;
      if (standardRoom && typeof standardRoom === "object") {
        Object.keys(standardRoom).forEach((key) => {
          // "standardRoom.E", "standardRoom.V" osv.
          unique.add(`standardRoom.${key}`);
        });
      }
    });

    return Array.from(unique).sort();
  }
);

// Hjelp: norsk tittel basert på nøkkelen
const titleForStandardRoomKey = (key: `standardRoom.${string}`): string => {
  const disiplin = key.split(".")[1] ?? "";
  return `Standardrom – validering (${disiplin})`;
};

export const selectStandardRoomValidationColorItems = createSelector(
  [selectUniqueStandardRoomValidationsKeys],
  (keys): IRoomColoring[] => {
    return keys.map((key) => {
      const disiplin = key.split(".")[1] ?? "";

      return {
        id: ZRoomColoringId.parse(key), // f.eks. "standardRoom.E"
        title: titleForStandardRoomKey(key as `standardRoom.${string}`),

        categoryId: ERoomColoringCategoryId.DrofusStandardromValidering,
        subcategoryId: ERoomColoringSubcategoryId.ValideringStatus,
        order: 10,
        tags: ["drofus", "standardrom", "validering", disiplin],

        explanation: {
          shortNo: "Fargelegger rom etter treffprosent for standardromvalidering (intervaller).",
          longNo:
            "Dette viser hvor godt rommet matcher kravene i standardrom for valgt disiplin. Brukes for rask QA i plantegning.",
          docKey: "colorfilters/standardrom-validering",
        },

        colorSpecs: DROFUS_OCCURRENCE_VALIDATION_COLOR_SPECS,
        trendKeyPath: `validation.standardRoom.${disiplin}.summary.matchRatePct`,
        getPolygonColors: makeStandardRoomValidationColorFn(key as `standardRoom.${string}`),
      };
    });
  }
);

/* -------------------------------------------------------------------------- */
/*  Beslutning (validationDecisions) → dynamiske fargefilter                   */
/* -------------------------------------------------------------------------- */

export const DECISION_FILTER_PREFIX = "decision:" as const;

export type IDecisionFilterId = `${typeof DECISION_FILTER_PREFIX}${string}.${string}`;
// returnerer f.eks. ["decision:standardRoom.E", "decision:standardRoom.V"]

export const selectUniqueValidationDecisionKeys = createSelector(
  [selectHttpGetRoomsCurrentFloorResult],
  (roomsMap): IDecisionFilterId[] => {
    if (!roomsMap) return [];

    const keys = new Set<IDecisionFilterId>();

    for (const room of Object.values(roomsMap)) {
      const std = room.validationDecisions?.standardRoom;
      if (!std) continue;

      for (const discipline of Object.keys(std)) {
        keys.add(`${DECISION_FILTER_PREFIX}standardRoom.${discipline}`);
      }
    }

    return Array.from(keys).sort();
  }
);

const titleForDecisionKey = (key: string) => {
  // "standardRoom.E" -> "Beslutning – Standardrom (E)"
  const [branch, discipline] = key.split(".");
  if (branch === "standardRoom") return `Beslutning – Standardrom (${discipline})`;
  return `Beslutning – ${branch} (${discipline})`;
};

const toDecisionKey = (id: IDecisionFilterId) =>
  id.replace(DECISION_FILTER_PREFIX, "") as `${string}.${string}`;

export const selectValidationDecisionColorItems = createSelector(
  [selectUniqueValidationDecisionKeys],
  (keys): IRoomColoring[] => {
    return keys.map((id) => {
      const decisionKey = toDecisionKey(id);
      const [branch, disiplin] = decisionKey.split(".") as [string, string];

      return {
        id: ZRoomColoringId.parse(id), // "decision:standardRoom.E"
        title: titleForDecisionKey(decisionKey),

        categoryId: ERoomColoringCategoryId.DrofusStandardromValidering,
        subcategoryId: ERoomColoringSubcategoryId.ValideringStatus,
        order: 100,
        tags: ["beslutning", "validering", branch, disiplin],

        explanation: {
          shortNo:
            "Fargelegger rom etter gjeldende beslutningsstatus (godkjent / avvik / må sjekkes).",
          longNo:
            "Dette viser manuelt beslutningsutfall per disiplin. Brukes for å skille faglig vurdering fra automatisk matchprosent.",
          docKey: "colorfilters/beslutning",
        },

        colorSpecs: VALIDATION_DECISION_COLOR_SPECS,
        trendKeyPath: `validationDecisions.${decisionKey}`,
        getPolygonColors: makeValidationDecisionColorFn(decisionKey),
      };
    });
  }
);
